import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { COLLECTIONS, BILLING_DOCS } from './paths';
import { functions } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { entitlementAsOf } from '../domain/entitlement';
import { todayISO } from '../domain/clock';
import { browserTimeZone } from '../lib/intlOptions';
import { sayUnverified } from '../domain/clientEvents';
import { useCollectionWindow } from './useCollectionWindow';
import { useDocumentWindow } from './useDocumentWindow';

// What this studio's subscription to the app looks like, folded into one
// verdict.
//
// Two sources, both under users/{uid}: the account document `billing/account`
// (what Stripe has done, and when the trial ends) and the `billingGrants`
// collection (by-hand grants — a comp, an awarded month, a price held).
// `entitlementAsOf` in src/domain/entitlement.js folds them; this provider only
// reads them and hands the fold over, so every question of the form "may this
// studio use the app / what does it pay / how long has it got" has one answer.
//
// ── This provider never writes ──────────────────────────────────────────────
//
// firestore.rules carries `allow write: if false` on both paths: the writers
// are the Stripe webhook, the callable that stamps a new account, and the admin
// CLI. A client write path would let a console award itself a comp, so this
// context has no save function at all. The Membership tab changes a
// subscription by asking a Cloud Function to start a Stripe session.
//
// ── `loading` is derived ────────────────────────────────────────────────────
//
// Same shape and reason as src/data/useSettings.jsx: a provider that reports
// "loaded" while holding defaults loses data. So "whose record is in hand" and
// "what is in hand" are one value, `{ uid, ... }`, and `loading` is computed
// from it rather than assigned by a separate `setLoading`.
//
// An unread account folds to `unknown`, which is entitled — the fold fails open
// (ENTITLED_STATES in domain/billingPlans.js) — so nothing locks. But the
// Membership tab would say "Setting up" to a paying studio for as long as the
// snapshot took, and the trial banner would flicker. `loading` holds both back.
//
// `undecided` below covers the window where the record has been read and there
// is nothing in it.

const EntitlementContext = createContext(null);

// The cap is here so a corrupted account cannot make the app open an
// unbounded read at startup, matching TRACK_LIMIT in data/useCategories.jsx.
const GRANT_LIMIT = 200;

// When the client asks the server to stamp a missing account: at once, then
// after a cold start has had time to finish, then once more. See the effect
// that uses it.
const ENSURE_ATTEMPTS_MS = [0, 5000, 30000];

// A Firebase callable error code meaning the function is not deployed. Every
// call site here survives a callable's absence rather than blocking on it: the
// Membership tab renders and says the payment service is not deployed.
export const NOT_DEPLOYED = 'functions/not-found';

// Ask the server for a callable, or null if this build has no Functions at all
// (an unconfigured deployment; the integration harness, whose firebase stand-in
// exports `functions = null`). Callers treat null as "not available", which is
// the same branch as "not deployed".
//
// A refusal firebase-functions makes itself — App Check, or an ID token that
// did not verify — rejects with the bare word "Unauthenticated", which is
// replaced here, once for every callable, with the core's sentence (#493).
export function callable(name) {
  if (!functions) return null;
  const call = httpsCallable(functions, name);
  return (data) => call(data).then(null, (err) => { throw sayUnverified(err); });
}

/**
 * The sign-up's own `ensureAccount` call (src/context/AuthContext.jsx), with
 * this browser's zone and whatever else it is handed — the Meta Conversions
 * API's consent answer (#781). Resolves to the result, or null when there are
 * no Functions or the call failed; never rejects. The stamp it may make is the
 * same idempotent one the provider below asks for.
 */
export function ensureAccountAtSignUp(extra = {}) {
  const ensureAccount = callable('ensureAccount');
  if (!ensureAccount) return Promise.resolve(null);
  return ensureAccount({ timeZone: browserTimeZone(), ...extra }).then((res) => res, () => null);
}

// Classify a failed callable so a component can say something true about it.
// `not-deployed` is a function this deployment does not have; `unverified` is
// firebase-functions' own refusal, whose sentence is the error's message;
// anything else is a fault.
export function callableFailure(err) {
  if (!err) return null;
  if (err.code === NOT_DEPLOYED) return 'not-deployed';
  if (err.unverified === true) return 'unverified';
  return 'failed';
}

export function EntitlementProvider({ children }) {
  // `authLoading` is load-bearing exactly as it is in useSettings: without it a
  // signed-out studio and a session that has not resolved yet are both
  // `user == null`, and this would report a confident verdict for the second.
  const { user, loading: authLoading } = useAuth();
  const uid = user?.uid || null;

  // What has been read, and whose it is: a document held apart from the
  // identity it belongs to will eventually be attributed to the wrong studio.
  // Both windows scope `hasRead` to the uid they were read for.
  //
  // `billing/account` — one document, read as one (#207 U5), not as a
  // collection window over `billing/`. A window cannot say *the server told us
  // there is no account*: the store records an absent document as
  // `exists: false` so a single-document read can tell that from *we have never
  // been told*, and a window collapses both into "the list is empty". `hasRead`
  // with no account is what makes `no_account` an answer, and what keeps
  // `ensureAccount` below from firing at a studio whose account has not been
  // read yet. A window would also need a `limit()` (#64).
  const accountRead = useDocumentWindow({
    uid,
    collection: COLLECTIONS.billing,
    id: BILLING_DOCS.account,
  });
  // The billingGrants window (#207 U5).
  //
  // The document id is part of the grant. `foldGrants` sorts by `grantedOn`
  // and breaks a same-day tie on `String(a.id).localeCompare(String(b.id))`;
  // without ids the sort is a no-op and Firestore's return order decides, so a
  // `comp_start` and a `comp_end` written on one day fold to `compOpen: false`
  // one way round and `true` the other. The ids the CLI writes sort into the
  // intended order (`comp_start` ahead of `comp_end`). The hook's default row
  // carries `id`.
  const grantsWindow = useCollectionWindow({
    uid,
    collection: COLLECTIONS.billingGrants,
    limit: GRANT_LIMIT,
  });

  // Whether the `ensureAccount` attempts have already been started for this
  // uid, and how they went. A ref rather than state for the first: it is read
  // and written inside the snapshot handler, and a re-render must not be able
  // to reset it into starting the series again.
  const requestedFor = useRef(null);
  const [ensureError, setEnsureError] = useState(null);
  // Mirrors of `hasAccount` and the pending timers, for the retry closures
  // below: a timer fires with whatever `hasAccount` was when it was set, and
  // it has to ask what it is *now*.
  const hasAccountRef = useRef(false);
  const ensureTimers = useRef([]);

  useEffect(() => {
    // Whatever was asked for belongs to whoever was signed in a moment ago.
    requestedFor.current = null;
    setEnsureError(null);
    // Keyed on the uid rather than the user object: onAuthStateChanged hands
    // back a fresh object for the same person, and resetting for that would
    // start the `ensureAccount` series again for no reason.
  }, [uid]);

  // A document that is not there still counts as **read**: "this studio has no
  // account" is the answer that decides whether to ask the server to stamp one.
  //
  // A read this studio is not allowed to make is not a crash. The rules scope
  // both paths to the owner, so that only happens on a signed-out race or a
  // misconfigured deployment; a window that never arrives leaves `hasRead`
  // false and `loading` true, which is the safe answer for the gate.
  const hasRead = Boolean(uid) && accountRead.hasRead && grantsWindow.hasRead;
  // `hasRead` is the receipt, `data` is the document. A studio the server says
  // has no account reads `hasRead: true` with `data: null`, which is the
  // answer `no_account` is built on.
  //
  // It has to be the receipt and not `!documentIsLoading` (#279): that is
  // **true before the first read comes back**, so this could pair a `stale`
  // grants receipt from a previous session with an account document nobody had
  // read yet and fire `ensureAccount` at a studio that has one. `hasAnswer` is
  // not what a creation should gate on either.
  const account = hasRead ? accountRead.data : null;
  const hasAccount = hasRead && account !== null;
  hasAccountRef.current = hasAccount;

  // ── Stamping an account that is not there ─────────────────────────────────
  //
  // Signing up creates an auth user; it does not create the billing document,
  // because the trial's length must be decided by a *server* clock (see
  // `newAccount` in domain/entitlement.js). So the client notices the document
  // is missing and asks.
  //
  // The Auth `onCreate` trigger (functions/index.js `stampAccountOnSignup`)
  // stamps the account within seconds of sign-up whether or not a browser is
  // open. This call covers that trigger failing; no periodic sweep does.
  //
  // Three attempts — now, five seconds on, thirty seconds on — and then it
  // stops. A cold-started callable on a first load fails often, and until the
  // document exists the Membership tab says "Setting up". The series is bounded
  // because the account listener fires on every grant write and metadata
  // change; a call per snapshot while the function is unavailable would never
  // end. The ref guards against restarting it.
  //
  // Each attempt checks, when it fires, that this is still the same studio and
  // that the document has not arrived in the meantime; the callable is
  // idempotent, so a late duplicate is harmless.
  useEffect(() => {
    if (!hasRead || hasAccount) return;
    if (requestedFor.current === uid) return;
    requestedFor.current = uid;

    const ensureAccount = callable('ensureAccount');
    if (!ensureAccount) {
      // No Functions in this build at all. The studio has no account
      // document, which the verdict already reports as `no_account`.
      setEnsureError({ code: NOT_DEPLOYED });
      return;
    }

    const attempt = () => {
      if (requestedFor.current !== uid || hasAccountRef.current) return;
      // Two arguments, not `.then(…).catch(…)` (#184): the trailing form would
      // report a bug in the success handler as an entitlement failure, which a
      // teacher is shown.
      // With this browser's zone, the calendar the server reads the sign-up
      // instant on when the studio has none stored yet (#645). A zone, never
      // a date: the instant is Firebase Auth's.
      ensureAccount({ timeZone: browserTimeZone() }).then(
        () => setEnsureError(null),
        (err) => setEnsureError(err),
      );
    };
    ensureTimers.current.forEach(clearTimeout);
    ensureTimers.current = ENSURE_ATTEMPTS_MS.map((delay) => setTimeout(attempt, delay));
  }, [hasRead, hasAccount, uid]);

  // A provider that unmounts takes its pending attempts with it. In the app
  // that is a tab closing; in a test it is what keeps a thirty-second timer
  // from outliving the suite.
  useEffect(() => () => ensureTimers.current.forEach(clearTimeout), []);

  const value = useMemo(() => {

    const grants = hasRead ? grantsWindow.docs : [];

    // The studio's own calendar day, on the UTC grid the whole domain uses.
    //
    // Computed here, and recomputed only when this memo re-runs, so a tab left
    // open across midnight keeps yesterday's verdict until something else
    // re-renders it. The difference is one day at the very end of a trial, and
    // the server is the authority on access; no timer is spent on it.
    //
    // The server dates every grant on the studio's calendar — its stored
    // zone, or the UTC day without one (functions/billing/studioDay.js) — so
    // a grant made today is in this fold the moment it lands, at any hour,
    // for a teacher whose device keeps the studio's zone (#617). Dated on the
    // UTC day, from 17:00 to midnight in Los Angeles a grant just made would
    // be dated tomorrow and the fold below would ignore it.
    const today = todayISO();
    const verdict = entitlementAsOf({ account, grants, today });

    // Derived, never assigned — see the header. Signed out is *not* loading:
    // there is no studio to read and the empty verdict is the answer.
    const loading = authLoading || (Boolean(uid) && !hasRead);

    // ── Has the question actually been answered? ────────────────────────────
    //
    // `undecided` is neither `loading` nor `!canUse`. It answers a third
    // question: **do we actually know?**
    //
    // A studio between signing up and `ensureAccount` stamping its document has
    // been read — there is nothing there — and the fold reports
    // `state: 'unknown', reason: 'no_account'`. That state is *entitled*: the
    // gate fails open (ENTITLED_STATES in domain/billingPlans.js), so `canUse`
    // is true and nothing locks.
    //
    // `canUse` true because the account checks out, and `canUse` true because
    // there is nothing to check, are different facts. A caller that draws a
    // spinner rather than the feature, or must not print "your subscription is
    // fine", needs to tell them apart; this flag does.
    //
    // The fold and this flag each, independently, keep a slow write from
    // putting a paywall over a paying studio's calendar.
    const undecided = loading || (Boolean(uid) && !hasAccount);

    return {
      loading,
      undecided,
      today,

      // The fold, whole, plus the fields worth reading without reaching into
      // it. Nothing here recomputes any of them.
      verdict,
      state: verdict.state,
      reason: verdict.reason,
      canUse: verdict.canUse,
      daysLeft: verdict.daysLeft,
      accessUntil: verdict.accessUntil,
      plan: verdict.plan,
      priceMinor: verdict.priceMinor,
      currency: verdict.currency,

      // The raw account document, for the two questions the verdict
      // deliberately does not answer: whether this studio already exists at
      // Stripe (which decides checkout-versus-portal) and whether it has ever
      // been charged. Null when there is none.
      account,
      hasAccount,

      // Why there is no account document, when there is none.
      ensureError,
      ensureFailure: callableFailure(ensureError),
    };
  }, [hasRead, hasAccount, account, grantsWindow.docs, authLoading, uid, ensureError]);

  return <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>;
}

export function useEntitlement() {
  const ctx = useContext(EntitlementContext);
  if (!ctx) throw new Error('useEntitlement must be used within an EntitlementProvider');
  return ctx;
}

// ─── The enforcement seam ───────────────────────────────────────────────────
//
// The hook a feature is gated with: a `<Paywall>` around a route.
//
// Three answers, not two. `blocked` is *not* `!allowed`: a studio whose record
// has not been read yet is neither, and treating it as blocked would let a
// cold load lock a paying teacher out of their calendar.
export function useCanUse() {
  const { canUse, undecided, state, reason, verdict } = useEntitlement();
  return useMemo(() => ({
    // May this studio use the app? True only once we know.
    allowed: !undecided && canUse,
    // Should something be locked? True only once we know it should be.
    blocked: !undecided && !canUse,
    // We do not know yet. Show the feature; the server is the authority on
    // anything that actually costs money.
    undecided,
    state,
    reason,
    verdict,
  }), [canUse, undecided, state, reason, verdict]);
}
